import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  LineChart,
  Line,
  PieChart,
  Pie,
  Tooltip,
  Legend,
  CartesianGrid,
  XAxis,
  YAxis,
  AreaChart,
  Area,
  ScatterChart,
  Scatter,
  ZAxis,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Treemap,
  FunnelChart,
  Funnel,
  Sector,
} from "recharts";
import SimpleTable from "./Table"; // Assuming this is your SimpleTable path
import {
  BarChart as BarIcon,
  ShowChart as LineIcon,
  PieChart as PieIcon,
  TableChart as TableIcon,
  StackedLineChart as AreaIcon,
  ScatterPlot as ScatterIcon,
  DonutSmall as DonutIcon,
  Timeline as FunnelIcon,
  BubbleChart as BubbleIcon,
  Radar as RadarIcon,
  Layers as TreemapIcon,
  GridOn as HeatmapIcon,
} from "@mui/icons-material";

const COLORS = [
  "#2563eb",
  "#9333ea",
  "#db2777",
  "#fde047",
  "#34d399",
  "#f97316",
  "#8b5cf6",
  "#ef4444",
  "#10b981",
  "#f59e0b",
];

const chartIcons = {
  bar: <BarIcon fontSize="small" sx={{ mr: 1 }} />,
  line: <LineIcon fontSize="small" sx={{ mr: 1 }} />,
  pie: <PieIcon fontSize="small" sx={{ mr: 1 }} />,
  donut: <DonutIcon fontSize="small" sx={{ mr: 1 }} />,
  area: <AreaIcon fontSize="small" sx={{ mr: 1 }} />,
  scatter: <ScatterIcon fontSize="small" sx={{ mr: 1 }} />,
  bubble: <BubbleIcon fontSize="small" sx={{ mr: 1 }} />,
  radar: <RadarIcon fontSize="small" sx={{ mr: 1 }} />,
  treemap: <TreemapIcon fontSize="small" sx={{ mr: 1 }} />,
  funnel: <FunnelIcon fontSize="small" sx={{ mr: 1 }} />,
  histogram: <BarIcon fontSize="small" sx={{ mr: 1 }} />,
  table: <TableIcon fontSize="small" sx={{ mr: 1 }} />,
  heatmap: <HeatmapIcon fontSize="small" sx={{ mr: 1 }} />,
};

export default function DynamicChartViewer({
  possibleCharts,
  data,
  xAxis,
  yAxis,
  title,
  darkMode,
}) {
  const [chartType, setChartType] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  // Normalization ONLY for charts (not tables): Handle case-insensitive keys, force numeric Y.
  // For non-numeric Y, default to 0 (avoids NaN in charts).
  const normalizedData = data?.map((d) => {
    const xKey = Object.keys(d).find(
      (k) => k.toLowerCase() === xAxis.toLowerCase()
    );
    const yKey = Object.keys(d).find(
      (k) => k.toLowerCase() === yAxis.toLowerCase()
    );
    const yValue = d[yKey];
    return {
      [xAxis]: d[xKey] ?? "",
      [yAxis]: isNaN(Number(yValue)) ? 0 : Number(yValue), // Avoid NaN for charts
    };
  });

  // For pie/donut total, use normalized Y values
  const total = normalizedData.reduce((sum, d) => sum + (d[yAxis] || 0), 0);

  const graphs = possibleCharts.filter((t) =>
    [
      "bar",
      "line",
      "pie",
      "donut",
      "area",
      "scatter",
      "bubble",
      "radar",
      "treemap",
      "funnel",
      "waterfall",
      "heatmap",
      "histogram",
      "table",
    ].includes(t.toLowerCase())
  );

  useEffect(() => {
    if (!chartType && graphs.length) setChartType(graphs[0].toLowerCase());
  }, [graphs, chartType]);

  if (!data?.length || graphs.length === 0)
    return (
      <Typography sx={{ textAlign: "center", py: 3, color: "gray" }}>
        No data to display
      </Typography>
    );

  const axisColor = darkMode ? "#e2e8f0" : "#1e293b";
  const gridColor = darkMode ? "#475569" : "#e2e8f0";
  const bgColor = darkMode ? "#1e293b" : "#f8fafc";
  const barHover = darkMode ? "rgba(27, 38, 53, 0.85)" : "rgba(0, 0, 0, 0.2)";

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const { name, value } = payload[0];
    const percent = ["pie", "donut"].includes(chartType)
      ? ` (${((value / total) * 100).toFixed(1)}%)`
      : "";
    return (
      <Box
        sx={{
          bgcolor: darkMode ? "#334155" : "#fff",
          p: 1.5,
          borderRadius: 2,
          color: darkMode ? "#e2e8f0" : "#1e293b",
          boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
        }}
      >
        <Typography sx={{ fontSize: 13, fontWeight: "bold" }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: 12 }}>
          {`${name}: ${value.toLocaleString("en-US", {
            maximumFractionDigits: 2,
          })}${percent}`}
        </Typography>
      </Box>
    );
  };

  const activePie = (props) => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } =
      props;
    return (
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 10}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
        cornerRadius={5}
      />
    );
  };

  const renderChart = () => {
    const commonProps = {
      data: normalizedData,
      margin: { top: 20, right: 15, bottom: 10, left: 0 },
      animationDuration: 800,
      animationEasing: "ease-out",
    };
    const axisProps = {
      stroke: axisColor,
      tick: { fill: axisColor, fontSize: 12 },
    };

    switch (chartType) {
      case "bar":
      case "histogram":
      case "waterfall":
        return (
          <BarChart {...commonProps}>
            <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
            <XAxis
              dataKey={xAxis}
              {...axisProps}
              tick={{ fill: axisColor, fontSize: 12, fontWeight: 500 }}
            />
            <YAxis
              {...axisProps}
              tick={{ fill: axisColor, fontSize: 12, fontWeight: 500 }}
            />
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: barHover, stroke: "none", opacity: 1 }}
            />
            <Legend wrapperStyle={{ color: axisColor, fontSize: 12 }} />
            <Bar dataKey={yAxis} radius={[5, 5, 0, 0]}>
              {normalizedData.map((d, i) => (
                <Cell
                  key={i}
                  fill={COLORS[i % COLORS.length]}
                  cursor="pointer"
                  stroke={darkMode ? "#1e293b" : "#fff"}
                />
              ))}
            </Bar>
          </BarChart>
        );

      case "heatmap":
        const maxValue = Math.max(...normalizedData.map((d) => d[yAxis]));
        return (
          <ScatterChart {...commonProps}>
            <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
            <XAxis type="category" dataKey={xAxis} {...axisProps} />
            <YAxis type="category" dataKey={yAxis} {...axisProps} />
            <Tooltip content={<CustomTooltip />} />
            <Scatter
              data={normalizedData}
              shape={(props) => {
                const { cx, cy, payload } = props;
                const value = payload[yAxis];
                const intensity = Math.round((value / maxValue) * 255);
                const color = `rgb(255, ${255 - intensity}, ${
                  255 - intensity
                })`;
                return (
                  <rect
                    x={cx - 20}
                    y={cy - 20}
                    width={40}
                    height={40}
                    fill={color}
                    stroke="#fff"
                  />
                );
              }}
            />
          </ScatterChart>
        );

      case "line":
        return (
          <LineChart {...commonProps}>
            <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
            <XAxis dataKey={xAxis} {...axisProps} />
            <YAxis {...axisProps} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ color: axisColor, fontSize: 12 }} />
            <Line
              type="monotone"
              dataKey={yAxis}
              stroke={COLORS[0]}
              strokeWidth={2}
              dot={{ fill: COLORS[0] }}
            />
          </LineChart>
        );

      case "area":
        return (
          <AreaChart {...commonProps}>
            <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
            <XAxis dataKey={xAxis} {...axisProps} />
            <YAxis {...axisProps} />
            <Tooltip content={<CustomTooltip />} />
            <Area dataKey={yAxis} fill={COLORS[0]} stroke={COLORS[0]} />
          </AreaChart>
        );

      case "pie":
      case "donut":
        const showLabels = normalizedData.length <= 10; // show labels only if ≤ 10 slices

        return (
          <PieChart>
            <Tooltip content={<CustomTooltip />} />
            <Pie
              data={normalizedData}
              dataKey={yAxis}
              nameKey={xAxis}
              cx="50%"
              cy="50%"
              innerRadius={chartType === "donut" ? 60 : 0}
              outerRadius="80%"
              fill={COLORS[0]}
              stroke="none"
              animationDuration={500}
              animationEasing="ease-out"
              activeIndex={activeIndex}
              activeShape={activePie}
              onMouseEnter={(_, index) => setActiveIndex(index)}
              labelLine={false} // ❌ Remove arrow/line
              label={
                showLabels
                  ? ({
                      cx,
                      cy,
                      midAngle,
                      innerRadius,
                      outerRadius,
                      index,
                      name,
                      value,
                    }) => {
                      const RADIAN = Math.PI / 180;
                      const radius =
                        innerRadius + (outerRadius - innerRadius) / 2;
                      const x = cx + radius * Math.cos(-midAngle * RADIAN);
                      const y = cy + radius * Math.sin(-midAngle * RADIAN);
                      const percent = ((value / total) * 100).toFixed(1);

                      return (
                        <text
                          x={x}
                          y={y}
                          fill="#fff"
                          textAnchor="middle"
                          dominantBaseline="central"
                          fontSize={12}
                          fontWeight={600}
                        >
                          {`${name}: ${percent}%`}
                        </text>
                      );
                    }
                  : false
              } // hide labels if slices > 10
            >
              {normalizedData.map((entry, index) => (
                <Cell key={index} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
          </PieChart>
        );

      case "scatter":
      case "bubble":
        return (
          <ScatterChart {...commonProps}>
            <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
            <XAxis
              type={isNaN(normalizedData[0][xAxis]) ? "category" : "number"}
              dataKey={xAxis}
              {...axisProps}
            />
            <YAxis type="number" dataKey={yAxis} {...axisProps} />
            {chartType === "bubble" && (
              <ZAxis dataKey={yAxis} range={[50, 500]} />
            )}
            <Tooltip
              cursor={{ strokeDasharray: "3 3" }}
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const d = payload[0].payload;
                return (
                  <Box
                    sx={{
                      bgcolor: darkMode ? "#334155" : "#fff",
                      p: 1,
                      borderRadius: 2,
                    }}
                  >
                    <Typography sx={{ fontSize: 12 }}>
                      {`${xAxis}: ${d[xAxis]}`}
                    </Typography>
                    <Typography sx={{ fontSize: 12 }}>
                      {`${yAxis}: ${d[yAxis]}`}
                    </Typography>
                    {chartType === "bubble" && (
                      <Typography sx={{ fontSize: 12 }}>
                        {`Size: ${d[yAxis]}`}
                      </Typography>
                    )}
                  </Box>
                );
              }}
            />
            <Scatter
              name={title}
              data={normalizedData}
              fill={COLORS[0]}
              shape="circle"
            />
          </ScatterChart>
        );

      case "radar":
        return (
          <RadarChart outerRadius={100} data={normalizedData}>
            <PolarGrid />
            <PolarAngleAxis dataKey={xAxis} />
            <PolarRadiusAxis />
            <Radar
              name={title}
              dataKey={yAxis}
              stroke={COLORS[0]}
              fill={COLORS[0]}
              fillOpacity={0.6}
            />
            <Tooltip content={<CustomTooltip />} />
          </RadarChart>
        );

      case "treemap":
        return (
          <Treemap
            data={normalizedData}
            dataKey={yAxis}
            nameKey={xAxis}
            stroke="#fff"
            content={({ x, y, width, height, name, value, index }) => (
              <g>
                <rect
                  x={x}
                  y={y}
                  width={width}
                  height={height}
                  fill={COLORS[index % COLORS.length]}
                  stroke="#fff"
                />
                <text
                  x={x + width / 2}
                  y={y + height / 2}
                  textAnchor="middle"
                  fill="#fff"
                  fontSize={12}
                >
                  {`${name}: ${value}`}
                </text>
              </g>
            )}
          />
        );

      case "funnel":
        return (
          <FunnelChart>
            <Tooltip content={<CustomTooltip />} />
            <Funnel data={normalizedData} dataKey={yAxis} nameKey={xAxis} />
          </FunnelChart>
        );

      case "table":
        // CRITICAL FIX: Use ORIGINAL data for tables (full columns, no normalization)
        return (
          <SimpleTable
            data={data} // Original data with all columns
            darkMode={darkMode}
            title={title}
          />
        );

      default:
        return null;
    }
  };

  return (
   
    <Paper
      elevation={2}
      sx={{
        p: 2,
        width: chartType === "table" ? "fit-content" : "100%",
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: bgColor,
        display: "flex",
        flexDirection: "column",
        height: chartType === "table" ? "auto" : "70vh", // shrink for table
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
          mb: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: "bold", mr: 1, mb: 2, mt: 1 }}
        >
          🎯{" "}
          {title ||
            `${chartType.charAt(0).toUpperCase() + chartType.slice(1)} Chart`}
        </Typography>

        {graphs.length > 1 && (
          <FormControl size="small" sx={{ minWidth: 130, ml: 10, mr: 1 }}>
            <InputLabel>Chart Type</InputLabel>
            <Select
              value={chartType}
              onChange={(e) => setChartType(e.target.value)}
              label="Chart Type"
            >
              {graphs.map((t) => (
                <MenuItem
                  key={t}
                  value={t.toLowerCase()}
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  {chartIcons[t.toLowerCase()] || null}
                  <Typography sx={{ lineHeight: 1 }}>
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Box>

      <Box
        sx={{
          flex: chartType === "table" ? "0 0 auto" : 1,
          minHeight: chartType === "table" ? "auto" : 0,
          width: chartType === "table" ? "fit-content" : "100%",
          overflowX: chartType === "table" ? "auto" : "hidden",
          overflowY: chartType === "table" ? "auto" : "hidden",
        }}
      >
        {chartType === "table" ? (
          <Box sx={{ display: "inline-block" }}>{renderChart()}</Box>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            {renderChart()}
          </ResponsiveContainer>
        )}
      </Box>
    </Paper>
  );
}
